import { forwardRef, ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  children: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-brand-lime hover:bg-brand-lime-hover text-brand-ink shadow-sm hover:shadow-md disabled:bg-brand-lime/60',
  secondary: 'border-2 border-brand-ink text-brand-ink bg-transparent hover:bg-brand-ink hover:text-white disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent',
  tertiary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 disabled:bg-gray-50 disabled:text-gray-400',
  destructive: 'bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800 border border-red-200 hover:border-red-300 disabled:bg-red-50 disabled:text-red-300',
  ghost: 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:text-gray-400'
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm leading-compact',
  md: 'h-11 px-4 text-sm leading-body',
  lg: 'h-11 px-6 text-base leading-body'
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={`
          inline-flex items-center justify-center gap-2
          rounded-full font-bold
          transition-all duration-200
          focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink focus-visible:ring-offset-2
          disabled:cursor-not-allowed disabled:opacity-60
          ${variantClasses[variant]}
          ${sizeClasses[size]}
          ${className}
        `}
        {...props}
      >
        {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
